import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { SharesDialogProps } from '../types';

/** Доли договорные и из размера вкладов не выводятся (см. ADR 0020). */
export function SharesDialog({
  isDark,
  isOpen,
  onOpenChange,
  founders,
  onUpdate,
}: SharesDialogProps) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDrafts(Object.fromEntries(founders.map((f) => [f.id, String(f.profit_share)])));
  }, [isOpen, founders]);

  const totalShare = useMemo(
    () =>
      founders.reduce((acc, founder) => {
        const value = parseFloat(drafts[founder.id] ?? String(founder.profit_share));
        return acc + (Number.isNaN(value) ? 0 : value);
      }, 0),
    [drafts, founders],
  );

  const handleSave = async (id: string) => {
    const value = parseFloat(drafts[id]);
    if (Number.isNaN(value) || value < 0 || value > 100) return;
    setSavingId(id);
    try {
      await onUpdate(id, value);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-lg', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            Доли в прибыли
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            Договорная величина: от размера вкладов не зависит. Вклады возвращаются
            каждому полностью и отдельно.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {founders.map((founder) => (
            <div
              key={founder.id}
              className={cn(
                'flex items-center justify-between gap-4 rounded-xl p-3',
                isDark ? 'bg-slate-800/50' : 'bg-slate-50',
              )}
            >
              <p className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                {founder.name}
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={drafts[founder.id] ?? ''}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [founder.id]: e.target.value }))
                    }
                    className={cn(
                      'w-24',
                      isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
                    )}
                  />
                  <span className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>%</span>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleSave(founder.id)}
                  disabled={
                    savingId === founder.id || drafts[founder.id] === String(founder.profit_share)
                  }
                  className="bg-[#C9A227] hover:bg-[#C9A227]/90"
                >
                  {savingId === founder.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Сохранить
                </Button>
              </div>
            </div>
          ))}

          {Math.abs(totalShare - 100) > 0.01 && (
            <p className="text-xs text-amber-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Сумма долей — {totalShare.toFixed(2)}%, а должна быть 100%
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
